import { ESubscriptionStatus } from '@common/enums/subscription-status.enum';
import { CustomEventInput, ICalendarDateRange } from '@common/general-ui/full-calendar/full-calendar.component';
import { t } from '@common/hooks/use-t.hook';
import { IIdObject } from '@common/interfaces/id-object.interface';
import { generateId } from '@common/utils/generate-id.util';
import dayjs from 'dayjs';
import { formatDate } from '@common/utils/format-date/format-date.util';
import { getNow } from '@common/utils/get-now/now.util';
import { throwException } from '@common/utils/throw-exception.util';

export enum ECalendarEventType {
    subscription = 'subscription',
    order = 'order',
}

export interface IPauseCommon {
    pauseBillingDate: Date;
    resumeBillingDate: Date;
}

export interface ISubscriptionCommon extends IIdObject {
    status?: ESubscriptionStatus;
    deleted?: boolean;
    cutOff: number;
    cutoffTime: Date | undefined;
    cutOffDayOfWeekFromDC: number;
    nextBillingAtDate?: Date;
    subscription_items?: Array<{ item_price_id?: string }>;
    startDate?: Date;
    endDate?: Date;
    cancelDate?: Date;
    has_scheduled_changes?: boolean;
    subscription_with_scheduled_changes?: ISubscriptionCommon;
    pauses: IDayRangeCommon[] | undefined;
    totalPrice: number;
    totalAddonsPrice: number;
    billing_period?: number;
    billing_period_unit?: string;
}

export interface IItemPriceCommon {
    period?: number;
    period_unit?: string;
    description?: string;
}

export interface IDayRangeCommon {
    startDate?: Date;
    endDate?: Date;
}

export interface IOrderCommon {
    orderDate: Date;
    payment_status?: string;
    order_line_items?: { description?: string }[];
}

export const getStep = ({
    billing_period,
    billing_period_unit,
    isNewSubscription,
}: {
    billing_period: number;
    billing_period_unit: string | undefined;
    isNewSubscription: boolean;
}): number | void => {
    if (billing_period_unit === 'week') {
        return isNewSubscription ? 7 * Math.min(1, billing_period) : 7 * billing_period;
    } else if (billing_period_unit === 'day') {
        return billing_period;
    }
    throwException('Not implemented (get step)');
};

export enum EUpcomingDeliverySubType {
    upcomingDelivery = 'upcomingDelivery',
    pause = 'pause',
    skippedButPauseRemoved = 'skippedButPauseRemoved',
    blockedForUpdate = 'blockedForUpdate',
    delivered = 'delivered',
}

export interface ICalendarMetaData<
    TSubscription extends ISubscriptionCommon = ISubscriptionCommon,
    TPrice extends IItemPriceCommon = IItemPriceCommon,
    TOrder extends IOrderCommon = IOrderCommon,
> {
    type: ECalendarEventType;
    subscriptionObj?: {
        subType: EUpcomingDeliverySubType | undefined;
        subscription?: TSubscription;
        // subscriptionSkipObject: ICbSubscriptionSkip | undefined;
        subscriptionDeliveryDate: Date;
        subscriptionBillingDate_blockedOnUI: Date;
        subscriptionBillingDate: Date;
        totalPrice: number;
    };
    priceObj?: {
        price?: TPrice;
        subscriptionDeliveryDate: Date | undefined;
        subscriptionBillingDate_blockedOnUI: Date | undefined;
        subscriptionBillingDate: Date | undefined;
    };
    orderObj?: {
        order: TOrder | undefined;
        subscriptionBillingDate: Date | undefined;
        subscriptionBillingDate_blockedOnUI: Date | undefined;
    };
}

/**
 * Checks if a given day is the same as or after another day.
 * @param day1 - The first day to compare.
 * @param day2 - The second day to compare.
 * @returns A boolean indicating whether day1 is the same as or after day2.
 */
export const isSameOrAfter = (day1: dayjs.Dayjs, day2: Date | dayjs.Dayjs | undefined): boolean => {
    return day1.isSame(day2, 'days') || day1.isAfter(day2, 'days');
};
/**
 * Checks if a given day is the same as or before another day.
 * @param day1 - The first day to compare.
 * @param day2 - The second day to compare.
 * @returns A boolean indicating whether day1 is the same as or before day2.
 */
export const isSameOrBefore = (day1: dayjs.Dayjs, day2: Date | dayjs.Dayjs | undefined): boolean => {
    return day1.isSame(day2, 'days') || day1.isBefore(day2, 'days');
};
export type ICalendarEventCommon<
    TSubsr extends ISubscriptionCommon = ISubscriptionCommon,
    TPrice extends IItemPriceCommon = IItemPriceCommon,
    TOrder extends IOrderCommon = IOrderCommon,
> = CustomEventInput<ICalendarMetaData<TSubsr, TPrice, TOrder>> & IIdObject;

function getEventsFromOrders({
    rangeStartDate,
    rangeEndDate,
    orders,
    addAdditionalMinutesForBlockUIInMinutes,
}: {
    rangeStartDate: Date | undefined;
    rangeEndDate: Date | undefined;
    orders: IOrderCommon[];
    addAdditionalMinutesForBlockUIInMinutes: number;
}): ICalendarEventCommon[] {
    const res: ICalendarEventCommon[] = [];

    orders.forEach((order) => {
        const orderDate = dayjs(order.orderDate);

        if (isSameOrAfter(orderDate, rangeStartDate) && isSameOrBefore(orderDate, rangeEndDate)) {
            res.push({
                id: generateId('event'),
                title:
                    t('Order ') +
                    ` (${order?.payment_status}) ` +
                    order?.order_line_items?.map((i) => i.description).join(', '),
                allDay: true,
                editable: false,
                start: orderDate.toDate(),
                metadata: {
                    type: ECalendarEventType.order,
                    orderObj: {
                        order,
                        subscriptionBillingDate: orderDate.toDate(),
                        subscriptionBillingDate_blockedOnUI: dayjs(orderDate)
                            .subtract(addAdditionalMinutesForBlockUIInMinutes, 'minutes')
                            .toDate(),
                    },
                },
                extraDate: orderDate.toDate(),
            });
        }
    });
    return res;
}

function getEventsFromSubscriptions(
    today: Date,
    subscriptions: ISubscriptionCommon[],
    rangeStartDate: Date | undefined,
    rangeEndDate: Date | undefined,
    orderDates: Date[],
    addAdditionalMinutesForBlockUIInMinutes: number,
): ICalendarEventCommon[] {
    let res: ICalendarEventCommon[] = [];

    const activeSubscriptions = subscriptions.filter((s) => s.status !== ESubscriptionStatus.cancelled && !s.deleted);

    activeSubscriptions.forEach((subscription) => {
        res = [
            ...res,

            ...getEventsFromSubscription({
                today,
                subscription,
                rangeStartDate,
                rangeEndDate,
                orderDates,
                addAdditionalMinutesForBlockUIInMinutes,
            }),
        ];
    });

    return res;
}

function getEventsFromSubscription({
    today,
    subscription,
    rangeStartDate,
    rangeEndDate,
    orderDates,
    addAdditionalMinutesForBlockUIInMinutes,
}: {
    today: Date;
    subscription: ISubscriptionCommon;
    rangeStartDate: Date | undefined;
    rangeEndDate: Date | undefined;
    orderDates: Date[];
    addAdditionalMinutesForBlockUIInMinutes: number;
}): ICalendarEventCommon[] {
    const res: ICalendarEventCommon[] = [];

    const todayM = dayjs(today);
    const cutOff = subscription.cutOff;
    // const subscriptionStartDate = subscription.pause
    //     ? subscription.startDate
    // 	: subscription.nextBillingAtDate || subscription.startDate;

    // Warning!!! don't change to startDate - not working in a lot of cases!!!
    let subscriptionStartDate = subscription.nextBillingAtDate;
    // modify day of week
    subscriptionStartDate = dayjs(subscriptionStartDate)
        .set('day', subscription.cutOffDayOfWeekFromDC as number)
        .toDate();

    let step =
        getStep({
            billing_period: subscription.billing_period || 0,
            billing_period_unit: subscription.billing_period_unit,
            isNewSubscription: false,
        }) || Number.MAX_VALUE;
    // should coming from subscription or delivery configuration
    let current = dayjs(subscriptionStartDate); //.set('hours', 10).set('minutes', 0);
    current = current.set('hours', Number(subscription.cutoffTime?.getHours()));
    current = current.set('minutes', Number(subscription.cutoffTime?.getMinutes()));
    current = current.set('seconds', 0);
    while (current.isAfter(rangeStartDate)) {
        const prevTick = current.subtract(step, 'd');
        if (prevTick.isSame(subscription.startDate, 'd') || prevTick.isAfter(subscription.startDate)) {
            current = current.subtract(step, 'd');
        } else {
            break;
        }
    }

    let subscriptionEndDate = subscription.endDate;

    const pauses = subscription.pauses || [];

    while (isSameOrBefore(current, rangeEndDate)) {
        const pause = pauses.find((pause) => {
            const pauseEndDate = dayjs(pause?.endDate).subtract(1, 'm');
            return (
                current.isSame(pause.startDate, 'd') ||
                current.isSame(pauseEndDate) ||
                (current.isAfter(pause.startDate) && current.isBefore(pauseEndDate))
            );
        });

        const haveOrder = orderDates.find((orderDate) => current.isSame(orderDate, 'd'));
        if (!haveOrder) {
            if (
                isSameOrAfter(current, rangeStartDate) &&
                isSameOrBefore(current, subscriptionEndDate) &&
                (!subscription.cancelDate || (subscription.cancelDate && current.isBefore(subscription.cancelDate, 'd')))
            ) {
                const currentWithAdditionalMinutesForBlockUI = dayjs(current).subtract(
                    addAdditionalMinutesForBlockUIInMinutes,
                    'minutes',
                );

                let subType: EUpcomingDeliverySubType = EUpcomingDeliverySubType.upcomingDelivery;
                if (pause) {
                    subType = EUpcomingDeliverySubType.pause;
                } else if (current.isBefore(subscription.nextBillingAtDate, 'day') && current.isBefore(todayM)) {
                    // if (!isSameOrAfter(current, todayM))
                    {
                        subType = EUpcomingDeliverySubType.skippedButPauseRemoved;
                        // current = current.add(step, 'd');
                        // continue;
                    }
                } else {
                    subType =
                        todayM.isAfter(currentWithAdditionalMinutesForBlockUI) ||
                            todayM.isSame(currentWithAdditionalMinutesForBlockUI)
                            ? EUpcomingDeliverySubType.blockedForUpdate
                            : EUpcomingDeliverySubType.upcomingDelivery;
                }
                if (
                    subscription.has_scheduled_changes &&
                    isSameOrAfter(current, subscription.nextBillingAtDate) &&
                    subscription.subscription_with_scheduled_changes?.id
                ) {
                    res.push({
                        id: generateId('event'),
                        title: `${subscription.id}`,
                        start: current.toDate(),
                        allDay: true,
                        editable: false,
                        metadata: {
                            type: ECalendarEventType.subscription,
                            subscriptionObj: {
                                subscription: subscription.subscription_with_scheduled_changes,
                                subType,
                                subscriptionDeliveryDate: current.add(cutOff, 'd').toDate(),
                                subscriptionBillingDate: current.toDate(),
                                subscriptionBillingDate_blockedOnUI: dayjs(current)
                                    .subtract(addAdditionalMinutesForBlockUIInMinutes, 'minutes')
                                    .toDate(),
                                totalPrice: subscription?.totalPrice - (pause ? subscription.totalAddonsPrice : 0),
                            },
                        },
                        extraDate: current.toDate(),
                    });

                    step =
                        getStep({
                            billing_period: subscription.subscription_with_scheduled_changes?.billing_period || 0,
                            billing_period_unit: subscription.subscription_with_scheduled_changes?.billing_period_unit,
                            isNewSubscription: false,
                        }) || Number.MAX_VALUE;
                    subscriptionEndDate = subscription.subscription_with_scheduled_changes?.endDate;
                } else if (
                    !isSameOrAfter(current, subscription.nextBillingAtDate) &&
                    subType == EUpcomingDeliverySubType.upcomingDelivery
                ) {
                    // do nothing it's not possible
                    // upcoming delivery will be available only after nextBillingAt or in the same day
                } else {
                    const title = `${subscription.id}`;
                    res.push({
                        id: generateId('event'),
                        title,
                        start: current.toDate(),
                        allDay: true,
                        editable: false,
                        metadata: {
                            subscriptionObj: {
                                subscription,
                                subType,
                                subscriptionDeliveryDate: current.add(cutOff, 'd').toDate(),
                                subscriptionBillingDate: current.toDate(),
                                subscriptionBillingDate_blockedOnUI: dayjs(current)
                                    .subtract(addAdditionalMinutesForBlockUIInMinutes, 'minutes')
                                    .toDate(),
                                totalPrice: subscription?.totalPrice - (pause ? subscription.totalAddonsPrice : 0),
                            },

                            type: ECalendarEventType.subscription,
                        },
                        extraDate: current.toDate(),
                    });
                }
            }
        }
        current = current.add(step, 'd');
    }

    return res;
}

export function getEventsFromPrice({
    price,
    rangeStartDate,
    rangeEndDate,
    cutOff,
    isNewSubscription,
}: {
    price: IItemPriceCommon;
    rangeStartDate: Date | undefined;
    rangeEndDate: Date | undefined;
    cutOff: number;
    isNewSubscription: boolean;
    addAdditionalMinutesForBlockUIInMinutes: number;
}): ICalendarEventCommon[] {
    const res: ICalendarEventCommon[] = [];

    let current = rangeStartDate ? dayjs(rangeStartDate) : dayjs(getNow()).hour(23).minute(59).second(59);
    const step =
        getStep({ billing_period: price.period || 0, billing_period_unit: price.period_unit, isNewSubscription }) ||
        Number.MAX_VALUE;
    const subscriptionEndDate = new Date(8640000000000000);

    while (isSameOrBefore(current, rangeEndDate)) {
        if (isSameOrAfter(current, rangeStartDate) && isSameOrBefore(current, subscriptionEndDate)) {
            res.push({
                id: generateId('event'),
                title: formatDate(current.toDate()),
                start: current.toDate(),
                allDay: true,
                editable: false,
                metadata: {
                    type: ECalendarEventType.subscription,
                    priceObj: {
                        price,
                        subscriptionDeliveryDate: current.add(cutOff, 'd').toDate(),
                        subscriptionBillingDate: current.toDate(),
                        subscriptionBillingDate_blockedOnUI: dayjs(current).subtract(0, 'minutes').toDate(),
                    },

                    // subscriptionSkipObject: undefined,
                },
                extraDate: current.toDate(),
            });
        }
        current = current.add(step, 'd');
    }

    return res;
}

export const getAllEvents = ({
    today,
    subscriptions,
    range,
    orders,
    addAdditionalMinutesForBlockUIInMinutes,
}: {
    today: Date;
    range: ICalendarDateRange | undefined;
    subscriptions: ISubscriptionCommon[] | undefined;
    orders: IOrderCommon[] | undefined;
    addAdditionalMinutesForBlockUIInMinutes: number;
}): ICalendarEventCommon[] => {
    const subscriptionEvents = getEventsFromSubscriptions(
        today,
        subscriptions || [],
        range?.start,
        range?.end,
        orders?.map((o) => o.orderDate) || [],
        addAdditionalMinutesForBlockUIInMinutes,
    );

    const orderEvents = getEventsFromOrders({
        orders: orders || [],
        rangeStartDate: range?.start,
        rangeEndDate: range?.end,
        addAdditionalMinutesForBlockUIInMinutes,
    });
    return [...subscriptionEvents, ...orderEvents].sort((a, b) =>
        (a.start as Date).getTime && (b.start as Date).getTime && (a.start as Date)?.getTime() > (b.start as Date)?.getTime()
            ? 1
            : -1,
    );
};

export function getFirstEvent<T extends ICalendarEventCommon>(events: ICalendarEventCommon[]): T | undefined {
    const [firstEvent] = events
        .filter(
            (f) =>
                f.metadata?.type === ECalendarEventType.subscription &&
                f.metadata.subscriptionObj?.subType !== EUpcomingDeliverySubType.skippedButPauseRemoved,
        )
        // filter past skips
        .filter(
            (f) =>
                f.metadata?.subscriptionObj?.subType !== EUpcomingDeliverySubType.pause ||
                (f.metadata?.subscriptionObj?.subType === EUpcomingDeliverySubType.pause &&
                    f.metadata?.subscriptionObj.subscriptionBillingDate.getTime() > getNow().getTime()),
        );
    return firstEvent as T;
}
